import { ProviderId } from "@indeed/signals-schema/com/indeed/signals/schema/reactnative/ProviderId";
import { createJSTProctor } from "@indeed/tare/proctor/JSTProctor";
import { Platform } from "react-native";

import { getJSTReporter } from "../../error-reporting/JSTReporter";
import { RawNativeModules } from "../internal/TypedNativeModules";
import type { TareBridge } from "../types/TareBridge";
import { isTareCodegenActive, isTareCodegenActiveSync } from "./tareCodegenMigration";

function reportModelError(error: unknown, method: string): void {
  getJSTReporter(ProviderId.Unknown).error(error, { errDomain: "TareBridge", context: { method } });
}

// Preserve bridge identity for Suspense readiness; model values are never cached here.
const modelBridges = new WeakMap<TareBridge, TareBridge>();

/**
 * Native host defaults route model reads through the codegen rollout gate.
 * Web and explicitly injected bridges retain their own implementations.
 */
export function getTareBridge(): TareBridge | null {
  const bridge = RawNativeModules.RNTareBridge;
  if (!bridge || Platform.OS === "web") return bridge ?? null;
  const existing = modelBridges.get(bridge);
  if (existing) return existing;

  // Reads the raw bridge, not `modelBridge`. The proctor methods below are themselves gated, so a
  // proctor built over the routed bridge would recurse when the gate resolves its own allocation.
  const proctor = createJSTProctor(() => bridge);
  const modelMethods: TareBridge = {
    async getAccountStatus() {
      if (!(await isTareCodegenActive(proctor))) return bridge.getAccountStatus?.() ?? null;
      try {
        return RawNativeModules.NativeJSTModel?.getAccountStatus() ?? null;
      } catch (error) {
        reportModelError(error, "getAccountStatus");
        return null;
      }
    },
    getAccountStatusSync() {
      if (!isTareCodegenActiveSync(proctor)) return bridge.getAccountStatusSync?.() ?? null;
      try {
        return RawNativeModules.NativeJSTModel?.getAccountStatus() ?? null;
      } catch (error) {
        reportModelError(error, "getAccountStatusSync");
        return null;
      }
    },
    async getAppModel() {
      if (!(await isTareCodegenActive(proctor))) return bridge.getAppModel?.() ?? null;
      try {
        return (await RawNativeModules.NativeJSTModel?.getAppModel()) ?? null;
      } catch (error) {
        reportModelError(error, "getAppModel");
        return null;
      }
    },
    getAppModelSync() {
      if (!isTareCodegenActiveSync(proctor)) return bridge.getAppModelSync?.() ?? null;
      try {
        return RawNativeModules.NativeJSTModel?.getAppModelSync() ?? null;
      } catch (error) {
        reportModelError(error, "getAppModelSync");
        return null;
      }
    },
    async getAppSessionInfo() {
      if (!(await isTareCodegenActive(proctor))) return bridge.getAppSessionInfo?.() ?? null;
      try {
        return RawNativeModules.NativeJSTModel?.getAppSessionInfo() ?? null;
      } catch (error) {
        reportModelError(error, "getAppSessionInfo");
        return null;
      }
    },
    getAppSessionInfoSync() {
      if (!isTareCodegenActiveSync(proctor)) return bridge.getAppSessionInfoSync?.() ?? null;
      try {
        return RawNativeModules.NativeJSTModel?.getAppSessionInfo() ?? null;
      } catch (error) {
        reportModelError(error, "getAppSessionInfoSync");
        return null;
      }
    },
    async getDeviceModel() {
      if (!(await isTareCodegenActive(proctor))) return bridge.getDeviceModel?.() ?? null;
      try {
        return RawNativeModules.NativeJSTModel?.getDeviceModel() ?? null;
      } catch (error) {
        console.error(error);
        return null;
      }
    },
    getDeviceModelSync() {
      if (!isTareCodegenActiveSync(proctor)) return bridge.getDeviceModelSync?.() ?? null;
      try {
        return RawNativeModules.NativeJSTModel?.getDeviceModel() ?? null;
      } catch (error) {
        console.error(error);
        return null;
      }
    },
    async getProctorAllocation(testName) {
      if (!(await isTareCodegenActive(proctor)))
        return bridge.getProctorAllocation?.(testName) ?? null;
      try {
        return RawNativeModules.NativeJSTProctor?.getProctorAllocation(testName) ?? null;
      } catch (error) {
        reportModelError(error, "getProctorAllocation");
        return null;
      }
    },
    async getProctorAllocations(testNames) {
      if (!(await isTareCodegenActive(proctor)))
        return bridge.getProctorAllocations?.(testNames) ?? null;
      try {
        return (await RawNativeModules.NativeJSTProctor?.getProctorAllocations(testNames)) ?? null;
      } catch (error) {
        reportModelError(error, "getProctorAllocations");
        return null;
      }
    },
    async getProctorLoggingString() {
      if (!(await isTareCodegenActive(proctor))) return bridge.getProctorLoggingString?.() ?? null;
      try {
        return RawNativeModules.NativeJSTProctor?.getProctorLoggingString() ?? null;
      } catch (error) {
        reportModelError(error, "getProctorLoggingString");
        return null;
      }
    },
    getProctorLoggingStringSync() {
      if (!isTareCodegenActiveSync(proctor)) return bridge.getProctorLoggingStringSync?.() ?? null;
      try {
        return RawNativeModules.NativeJSTProctor?.getProctorLoggingString() ?? null;
      } catch (error) {
        reportModelError(error, "getProctorLoggingStringSync");
        return null;
      }
    },
  };
  // Bridgeless modules expose uncached methods on a native prototype; spreading drops them.
  const modelBridge = new Proxy(bridge, {
    get(target, property): unknown {
      return Object.hasOwn(modelMethods, property)
        ? Reflect.get(modelMethods, property)
        : Reflect.get(target, property);
    },
  });
  modelBridges.set(bridge, modelBridge);
  return modelBridge;
}
